/**
 * PROJECT REPORT
 *
 * Execution report view for an entire project.
 * Displays summary metrics, status distribution, and releases breakdown.
 */

import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { token } from '@atlaskit/tokens'
import Spinner from '@atlaskit/spinner'
import Lozenge from '@atlaskit/lozenge'

import StatusPieChart from './charts/StatusPieChart'
import PassRateGauge from './charts/PassRateGauge'
import StatusProgressBar from './charts/StatusProgressBar'
import MetricCardGrid, { createMetricsFromSummary, formatLastExecutionDate } from './cards/MetricCardGrid'
import ReportTabs from './ReportTabs'
import TrendsTab from './tabs/TrendsTab'
import DefectsTab from './tabs/DefectsTab'
import PeopleTab from './tabs/PeopleTab'
import TraceabilityTab from './tabs/TraceabilityTab'
import ReleasesComparisonTab from './tabs/ReleasesComparisonTab'
import {
  fetchProjectReportAsync,
  fetchAllReleasesReportAsync,
  selectProjectReport,
  selectReleasesSummary,
  selectCurrentReportTab,
  selectReportLoading,
  selectReportErrors,
  openDrillDown
} from '../../store/slices/reportsSlice'

/**
 * Release status to Lozenge appearance mapping
 */
const STATUS_APPEARANCE = {
  'Draft': 'default',
  'Active': 'inprogress',
  'Released': 'success',
  'Archived': 'moved'
}

/**
 * ProjectReport Component
 *
 * @param {object} props
 * @param {number|string} props.projectId - Jira project ID
 */
const ProjectReport = ({ projectId }) => {
  const dispatch = useDispatch()

  // Selectors
  const report = useSelector(selectProjectReport)
  const releasesSummary = useSelector(selectReleasesSummary)
  const currentTab = useSelector(selectCurrentReportTab)
  const loading = useSelector(selectReportLoading)
  const errors = useSelector(selectReportErrors)

  // Fetch overview + releases comparison when the project changes.
  useEffect(() => {
    if (projectId) {
      dispatch(fetchProjectReportAsync({ projectId }))
      dispatch(fetchAllReleasesReportAsync({ projectId }))
    }
  }, [dispatch, projectId])

  if (!report) {
    if (loading.project) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '300px'
        }}>
          <Spinner size="large" />
        </div>
      )
    }
    if (errors.project) {
      return (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          color: token('color.text.danger', '#DE350B')
        }}>
          <p>Failed to load project report</p>
          <p style={{ fontSize: '12px', color: token('color.text.subtle', '#6B778C') }}>
            {errors.project}
          </p>
        </div>
      )
    }
    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
        color: token('color.text.subtle', '#6B778C')
      }}>
        No report data available
      </div>
    )
  }

  const { summary } = report
  const metrics = createMetricsFromSummary(summary)

  const extendedMetrics = [
    ...metrics.slice(0, 3),
    { value: report.totalReleases, label: 'Releases' },
    { value: summary.totalScenarios, label: 'Scenarios' },
    ...metrics.slice(3)
  ]

  return (
    <div style={{ padding: '0' }}>
      {/* Header info — persistent across tabs. */}
      <div style={{
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: `1px solid ${token('color.border', '#DFE1E6')}`
      }}>
        <h3 style={{
          margin: 0,
          fontSize: '16px',
          fontWeight: 600,
          color: token('color.text', '#172B4D')
        }}>
          {report.projectName}
        </h3>
        <div style={{
          marginTop: '4px',
          fontSize: '13px',
          color: token('color.text.subtle', '#6B778C')
        }}>
          Project Key: {report.projectKey}
        </div>
      </div>

      <ReportTabs level="project" />

      {currentTab === 'trends' && (
        <TrendsTab level="project" id={projectId} />
      )}

      {currentTab === 'defects' && (
        <DefectsTab level="project" id={projectId} />
      )}

      {currentTab === 'people' && (
        <PeopleTab level="project" id={projectId} />
      )}

      {currentTab === 'traceability' && (
        <TraceabilityTab projectId={projectId} />
      )}

      {currentTab === 'releasesComparison' && (
        <ReleasesComparisonTab projectId={projectId} />
      )}

      {currentTab === 'overview' && (
        <>
      {/* Charts row */}
      <div style={{
        display: 'flex',
        gap: '32px',
        marginBottom: '24px',
        flexWrap: 'wrap',
        justifyContent: 'center'
      }}>
        {/* Status distribution chart — clicking a segment opens the drill-down panel. */}
        <div style={{ flex: '1 1 280px', minWidth: '280px', maxWidth: '350px' }}>
          <StatusPieChart
            statusDistribution={summary.statusDistribution}
            height={260}
            centerLabel="Executions"
            onSegmentClick={(statusName) =>
              dispatch(openDrillDown({ level: 'project', id: projectId, status: [statusName] }))
            }
          />
        </div>

        {/* Pass rate gauge */}
        <div style={{
          flex: '0 0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <PassRateGauge
            passRate={summary.passRate}
            size={180}
            showStatus={true}
          />
        </div>
      </div>

      {/* Metric cards */}
      <div style={{ marginBottom: '24px' }}>
        <MetricCardGrid metrics={extendedMetrics} compact={true} gap="12px" />
      </div>

      {/* Last execution info */}
      <div style={{
        marginBottom: '24px',
        fontSize: '13px',
        color: token('color.text.subtle', '#6B778C')
      }}>
        Last Execution: {formatLastExecutionDate(summary.lastExecutionDate)}
      </div>

      {/* Releases breakdown table */}
      {releasesSummary && releasesSummary.length > 0 && (
        <div>
          <h4 style={{
            margin: '0 0 12px 0',
            fontSize: '14px',
            fontWeight: 600,
            color: token('color.text', '#172B4D')
          }}>
            Releases Breakdown
          </h4>

          <div style={{
            border: `1px solid ${token('color.border', '#DFE1E6')}`,
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '13px'
            }}>
              <thead>
                <tr style={{
                  backgroundColor: token('color.background.neutral', '#F4F5F7')
                }}>
                  <th style={{
                    padding: '10px 12px',
                    textAlign: 'left',
                    fontWeight: 500,
                    color: token('color.text.subtle', '#6B778C'),
                    borderBottom: `1px solid ${token('color.border', '#DFE1E6')}`
                  }}>
                    Release
                  </th>
                  <th style={{
                    padding: '10px 12px',
                    textAlign: 'left',
                    fontWeight: 500,
                    color: token('color.text.subtle', '#6B778C'),
                    borderBottom: `1px solid ${token('color.border', '#DFE1E6')}`
                  }}>
                    Status
                  </th>
                  <th style={{
                    padding: '10px 12px',
                    textAlign: 'left',
                    fontWeight: 500,
                    color: token('color.text.subtle', '#6B778C'),
                    borderBottom: `1px solid ${token('color.border', '#DFE1E6')}`,
                    width: '180px'
                  }}>
                    Progress
                  </th>
                  <th style={{
                    padding: '10px 12px',
                    textAlign: 'right',
                    fontWeight: 500,
                    color: token('color.text.subtle', '#6B778C'),
                    borderBottom: `1px solid ${token('color.border', '#DFE1E6')}`
                  }}>
                    Pass Rate
                  </th>
                  <th style={{
                    padding: '10px 12px',
                    textAlign: 'right',
                    fontWeight: 500,
                    color: token('color.text.subtle', '#6B778C'),
                    borderBottom: `1px solid ${token('color.border', '#DFE1E6')}`
                  }}>
                    Test Cases
                  </th>
                </tr>
              </thead>
              <tbody>
                {releasesSummary.map((release, index) => (
                  <tr
                    key={release.releaseId}
                    style={{
                      backgroundColor: index % 2 === 0
                        ? token('elevation.surface', '#FFFFFF')
                        : token('color.background.neutral.subtle', '#FAFBFC')
                    }}
                  >
                    <td style={{
                      padding: '10px 12px',
                      color: token('color.text', '#172B4D'),
                      borderBottom: index < releasesSummary.length - 1
                        ? `1px solid ${token('color.border', '#DFE1E6')}`
                        : 'none'
                    }}>
                      {release.releaseName}
                      {release.archived && (
                        <span style={{
                          marginLeft: '8px',
                          fontSize: '11px',
                          color: token('color.text.subtle', '#6B778C')
                        }}>
                          (Archived)
                        </span>
                      )}
                    </td>
                    <td style={{
                      padding: '10px 12px',
                      borderBottom: index < releasesSummary.length - 1
                        ? `1px solid ${token('color.border', '#DFE1E6')}`
                        : 'none'
                    }}>
                      <Lozenge appearance={STATUS_APPEARANCE[release.releaseStatus] || 'default'}>
                        {release.releaseStatus}
                      </Lozenge>
                    </td>
                    <td style={{
                      padding: '10px 12px',
                      borderBottom: index < releasesSummary.length - 1
                        ? `1px solid ${token('color.border', '#DFE1E6')}`
                        : 'none'
                    }}>
                      <StatusProgressBar
                        statusDistribution={release.summary.statusDistribution}
                        height={8}
                      />
                    </td>
                    <td style={{
                      padding: '10px 12px',
                      textAlign: 'right',
                      fontWeight: 500,
                      color: release.summary.passRate >= 80
                        ? token('color.text.success', '#22A06B')
                        : release.summary.passRate >= 50
                          ? token('color.text.warning', '#FF991F')
                          : token('color.text.danger', '#DE350B'),
                      borderBottom: index < releasesSummary.length - 1
                        ? `1px solid ${token('color.border', '#DFE1E6')}`
                        : 'none'
                    }}>
                      {release.summary.passRate.toFixed(1)}%
                    </td>
                    <td style={{
                      padding: '10px 12px',
                      textAlign: 'right',
                      color: token('color.text.subtle', '#6B778C'),
                      borderBottom: index < releasesSummary.length - 1
                        ? `1px solid ${token('color.border', '#DFE1E6')}`
                        : 'none'
                    }}>
                      {release.summary.totalTestCases}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  )
}

export default ProjectReport
